import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Container, GlobalStyles, Skeleton, Stack, Typography } from '@mui/material';
// GraphQL
import { useApolloClient, useQuery } from '@apollo/client';
import { PUBLIC_FOLDER_FILES } from 'src/graphQL/queries';
// utils
import { fBytes, fCount } from 'src/utils/universeFormat';
// components
import Page from '../components/Page';
import useSafeTranslation from 'src/hooks/useSafeTranslation';
import useAuth from 'src/hooks/useAuth';
// universe
import { UniverseSkin } from 'src/components/universe/skin';
import WorkspaceCanvas from 'src/components/universe/workspace/WorkspaceCanvas';
import { InlineError, regionSheetSx } from 'src/components/universe/Surface';
import { folderLabelOf } from 'src/components/universe/fileKinds';
import {
  BRAND_LOGO_COLOR,
  BRAND_LOGO_WHITE,
  UNIVERSE_FONT,
  focusRingSx,
  hairlineOf,
  tabularSx
} from 'src/theme/universe/brand';
import { LAYER_FONT_STYLES, PAGE_FONT_SX } from 'src/theme/universe/pageFont';
import { LAYER_SCRIM_STYLES } from 'src/theme/universe/surfaceSx';
// sections
import FileGridView from 'src/sections/translatedFiles/view/FileGridView';
import AnalyticsWidgetSummary from 'src/sections/translatedFiles/AnalyticsWidgetSummary';

// ----------------------------------------------------------------------
// THE PUBLIC FOLDER PAGE (/files/:id/:fileType): the link a PM copies from a
// vault folder and sends, most often the client delivery. No sign-in; the
// visitor is the client.
//
// Simple on purpose (the owner, 29/10/2026: "troppo crowded la pagina,
// semplifica"): the folder's name, one line saying the order and what it
// holds, and the files as a list — each one opened by its name and saved by
// its Download. It is quick on a phone: it asks for the one folder the link
// names (PUBLIC_FOLDER_FILES), it is part of the entry bundle so its question
// leaves as soon as the app starts, the reader is fetched only when a file is
// opened, and public/index.html paints its frame and skips Google Maps before
// the app arrives.

// The folders a vault stores, and the @include flag that asks for each.
const FOLDER_FLAGS = {
  original: 'original',
  translated: 'translated',
  Support_Material: 'supportMaterial',
  project_manager: 'projectManager',
  Project_Manager: 'legacyProjectManager',
  formatted: 'formatted',
  client_delivery: 'clientDelivery',
  technician_Material: 'technicianMaterial',
  Proofreaded: 'proofreaded'
};

/**
 * The stored folder a link names. Some copy buttons lower-case the key, so
 * `support_material` is Support_Material; an exact key always wins.
 */
export const folderOfLink = (fileType) => {
  const asked = String(fileType || '');
  if (FOLDER_FLAGS[asked]) return asked;
  const lower = asked.toLowerCase();
  return Object.keys(FOLDER_FLAGS).find((key) => key.toLowerCase() === lower) || asked;
};

/** What the page asks the server: the vault, and that one folder. */
export const publicFolderVariables = (id, folder) => ({
  id,
  ...(FOLDER_FLAGS[folder] ? { [FOLDER_FLAGS[folder]]: true } : {})
});

/** A link to a vault that is not there, rather than a server that did not answer. */
const isMissing = (error) =>
  (error?.graphQLErrors || []).some(
    (one) =>
      one?.extensions?.code === 'BAD_USER_INPUT' ||
      /no file found|cast to objectid/i.test(one?.message || '')
  );

export default function TranslatedFiles() {
  return (
    <UniverseSkin>
      <PublicFolder />
    </UniverseSkin>
  );
}

function PublicFolder() {
  const theme = useTheme();
  const { t } = useSafeTranslation();
  const { id, fileType } = useParams();
  const folder = folderOfLink(fileType);
  // A visitor signed in to Universe in this browser: once the account is
  // known, the app clears the Apollo store (src/index.js, through
  // notificationAccountChanged), and a query already in flight is lost with
  // it — the page sat on its skeleton for good (29/10/2026). So the question
  // leaves only when the sign-in state is settled: at once for an anonymous
  // visitor, whose state is known from the first render; after that clear for
  // a signed-in one. A later clear (a sign-out in another tab) asks again.
  const { isInitialized } = useAuth();
  const client = useApolloClient();
  const { data, loading, error, refetch } = useQuery(PUBLIC_FOLDER_FILES, {
    variables: publicFolderVariables(id, folder),
    skip: !isInitialized,
    notifyOnNetworkStatusChange: true
  });
  useEffect(() => {
    if (!isInitialized) return undefined;
    return client.onClearStore(() => refetch());
  }, [client, isInitialized, refetch]);

  // Google Maps is not loaded under /files/ (public/index.html): whoever
  // goes on into the app from here gets it as this page closes.
  useEffect(
    () => () => {
      if (typeof window.loadGoogleMaps === 'function') window.loadGoogleMaps();
    },
    []
  );

  const vault = data?.projectFiles;
  const files = vault?.folders?.[folder] || [];
  const label = folderLabelOf(folder);
  const bytes = files.reduce((sum, file) => sum + (parseInt(file?.size, 10) || 0), 0);
  const waiting = (!isInitialized || loading) && !vault;

  // the one line under the title: the order, how many files, what they weigh
  const summary = [
    vault?.orderNumber || null,
    files.length
      ? files.length === 1
        ? t('publicFiles.oneFile', { defaultValue: '1 file' })
        : t('publicFiles.nFiles', { defaultValue: '{{n}} files', n: fCount(files.length) })
      : null,
    bytes ? fBytes(bytes) : null
  ]
    .filter(Boolean)
    .join(' · ');

  let body;
  if (waiting) {
    body = <PublicFolderSkeleton />;
  } else if (error && !vault) {
    const missing = isMissing(error);
    body = (
      <Box sx={{ ...regionSheetSx(theme), p: { xs: 2, sm: 2.5 } }}>
        <InlineError
          message={
            missing
              ? t('publicFiles.notFound', {
                  defaultValue: 'This folder could not be found. The link may be incomplete or no longer valid.'
                })
              : t('publicFiles.notLoaded', { defaultValue: 'This folder could not be loaded.' })
          }
          onRetry={missing ? undefined : () => refetch()}
        />
      </Box>
    );
  } else {
    body = (
      <>
        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <Typography
            component="h1"
            sx={{ m: 0, fontFamily: UNIVERSE_FONT, fontSize: { xs: 22, sm: 26 }, fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' }}
          >
            {label}
          </Typography>
          {summary && (
            <Typography
              sx={{ mt: 0.5, fontFamily: UNIVERSE_FONT, fontSize: 14, fontWeight: 500, color: 'text.secondary', ...tabularSx }}
            >
              {summary}
            </Typography>
          )}
        </Box>
        {files.length ? (
          <FileGridView tableData={files} />
        ) : (
          <Box sx={{ ...regionSheetSx(theme), px: 2.5, py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: UNIVERSE_FONT, fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
              {t('publicFiles.empty', { defaultValue: 'There are no files in this folder yet.' })}
            </Typography>
          </Box>
        )}
      </>
    );
  }

  return (
    <Page
      title={vault?.orderNumber ? `${label} · ${vault.orderNumber}` : label}
      // a folder shared by link is nobody's search result
      meta={<meta name="robots" content="noindex, nofollow" />}
    >
      <GlobalStyles styles={{ ...LAYER_FONT_STYLES, ...LAYER_SCRIM_STYLES }} />
      <WorkspaceCanvas />

      <Box component="header" sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${hairlineOf(theme)}` }}>
        <Container maxWidth="md" sx={{ height: { xs: 60, md: 72 }, display: 'flex', alignItems: 'center' }}>
          {/* the brand lock-up, not the bare globe: a client reads the name */}
          <Box
            component={RouterLink}
            to="/"
            sx={{ display: 'inline-flex', borderRadius: '4px', '&:focus-visible': focusRingSx(theme) }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'light' ? BRAND_LOGO_COLOR : BRAND_LOGO_WHITE}
              alt="LingoYou Universe"
              sx={{ display: 'block', height: { xs: 30, md: 34 }, width: 'auto' }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" component="main" sx={{ ...PAGE_FONT_SX, pt: { xs: 3, md: 5 }, pb: { xs: 4, md: 6 } }}>
        {body}
        {/* after the answer, like index.html's frame, so nothing moves twice */}
        {!waiting && <Usp />}
      </Container>
    </Page>
  );
}

/** LingoYou's three figures under the files: quiet, on the page's own ground. */
function Usp() {
  const theme = useTheme();
  const { t } = useSafeTranslation();
  return (
    <Box
      component="aside"
      sx={{
        mt: { xs: 4, sm: 5 },
        pt: { xs: 2.5, sm: 3 },
        borderTop: `1px solid ${hairlineOf(theme)}`,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        columnGap: { xs: 1.5, sm: 3 },
        alignItems: 'start'
      }}
    >
      <AnalyticsWidgetSummary title={t('publicFiles.brandLanguages', { defaultValue: 'LINGUE' })} total="+200" />
      <AnalyticsWidgetSummary title={t('publicFiles.brandAvailability', { defaultValue: 'DISPONIBILITÀ' })} total="24/7" />
      <AnalyticsWidgetSummary
        title={t('publicFiles.brandTranslators', { defaultValue: 'TRADUTTORI PROFESSIONISTI' })}
        total="20K"
      />
    </Box>
  );
}

/** The title and the rows in their own shapes, so nothing jumps when they land. */
function PublicFolderSkeleton() {
  const theme = useTheme();
  return (
    <Box aria-busy="true">
      <Skeleton variant="text" width={220} height={36} />
      <Skeleton variant="text" width={180} height={22} sx={{ mb: { xs: 2, sm: 2.5 } }} />
      <Box sx={regionSheetSx(theme)}>
        {[0, 1, 2, 3].map((n) => (
          <Stack
            key={n}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, ...(n > 0 && { borderTop: `1px solid ${hairlineOf(theme)}` }) }}
          >
            <Skeleton variant="rounded" width={36} height={36} />
            <Skeleton variant="text" sx={{ flex: 1 }} height={22} />
            <Skeleton variant="text" width={56} height={22} />
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
